import * as React from 'react';
import { ImageIcon, Store, ChevronRight, Heart } from 'lucide-react';
import { PhonePreview } from './DeviceMockup';
import type { BannerAction, SelectedProduct } from '../../stores/campaign-wizard.store';

// ============ Types ============

interface CarouselPreviewProps {
  imageUrl?: string | null;
  shopName?: string;
  action?: BannerAction;
  selectedProduct?: SelectedProduct | null;
  headline?: string;
  className?: string;
}

interface BannerPreviewProps {
  imageUrl?: string | null;
  title?: string;
  shopName?: string;
  className?: string;
}

// ============ Carousel Preview ============

export function CarouselPreview({
  imageUrl,
  shopName = 'Ma boutique',
  action,
  selectedProduct,
  headline,
  className,
}: CarouselPreviewProps) {
  const actionLabel = React.useMemo(() => {
    if (action?.type === 'product' && selectedProduct) {
      return `Voir ${selectedProduct.name}`;
    }
    return `Voir ${shopName}`;
  }, [action, selectedProduct, shopName]);

  return (
    <PhonePreview className={className}>
      {/* App header simulation */}
      <div className="bg-primary text-primary-foreground px-4 py-2.5">
        <span className="text-sm font-bold">Volovolo</span>
      </div>

      {/* Carousel section */}
      <div className="p-3">
        {/* Carousel card */}
        <div className="relative rounded-2xl overflow-hidden shadow-md bg-white">
          {/* Image */}
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Aperçu carousel"
              className="w-full aspect-square object-cover"
            />
          ) : (
            <div className="w-full aspect-square bg-muted flex items-center justify-center">
              <ImageIcon className="size-16 text-muted-foreground" />
            </div>
          )}

          {/* Action overlay */}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
            <div className="flex items-end justify-between text-white">
              <div className="flex-1 min-w-0 mr-2">
                {headline && (
                  <p className="text-sm font-bold leading-tight truncate mb-0.5">
                    {headline}
                  </p>
                )}
                <div className="flex items-center gap-1.5 text-xs font-normal opacity-90">
                  <Store className="size-3.5 shrink-0" />
                  <span className="truncate">{shopName}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full shrink-0">
                <span className="truncate max-w-[80px]">{actionLabel}</span>
                <ChevronRight className="size-4" />
              </div>
            </div>
          </div>

          {/* AD badge */}
          <div className="absolute top-3 right-3">
            <span className="text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded font-medium">
              AD
            </span>
          </div>
        </div>

        {/* Carousel dots */}
        <div className="flex justify-center gap-1.5 mt-3">
          <div className="w-6 h-2 rounded-full bg-primary" />
          <div className="size-2 rounded-full bg-gray-300" />
          <div className="size-2 rounded-full bg-gray-300" />
        </div>
      </div>

      {/* Fake product grid below */}
      <div className="px-3 pb-2">
        <div className="text-sm font-semibold mb-2">Populaires</div>
        <div className="grid grid-cols-2 gap-2">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className="aspect-square bg-gray-100" />
              <div className="p-2">
                <div className="h-2.5 bg-gray-200 rounded w-3/4 mb-1" />
                <div className="h-2.5 bg-gray-300 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </PhonePreview>
  );
}

// ============ Banner Preview (for category_banner) ============

export function BannerPreview({
  imageUrl,
  title,
  shopName = 'Ma boutique',
  className,
}: BannerPreviewProps) {
  return (
    <PhonePreview className={className}>
      {/* App header simulation */}
      <div className="bg-primary text-primary-foreground px-4 py-2.5">
        <span className="text-sm font-bold">Volovolo</span>
      </div>

      {/* Category header */}
      <div className="px-4 py-2.5 bg-white border-b">
        <span className="text-sm font-semibold">Mode & Accessoires</span>
      </div>

      {/* Banner */}
      <div className="p-3">
        <div className="relative rounded-2xl overflow-hidden shadow-md">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Aperçu bannière"
              className="w-full h-28 object-cover"
            />
          ) : (
            <div className="w-full h-28 bg-muted flex items-center justify-center">
              <ImageIcon className="size-10 text-muted-foreground" />
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />

          {/* Text overlay */}
          <div className="absolute inset-0 flex items-center p-4">
            <div className="text-white max-w-[70%]">
              {title ? (
                <p className="text-base font-bold drop-shadow-lg leading-tight line-clamp-2">
                  {title}
                </p>
              ) : (
                <p className="text-sm text-white/60 italic">
                  Ajoutez un texte...
                </p>
              )}
              <p className="text-xs mt-1.5 opacity-80">{shopName}</p>
            </div>
          </div>

          {/* AD badge */}
          <div className="absolute top-2 right-2">
            <span className="text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded font-medium">
              AD
            </span>
          </div>
        </div>
      </div>

      {/* Fake product list */}
      <div className="px-3 pb-2">
        <div className="grid grid-cols-2 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className="relative aspect-square bg-gray-100">
                <button className="absolute top-1.5 right-1.5 p-1">
                  <Heart className="size-4 text-gray-400" />
                </button>
              </div>
              <div className="p-2">
                <div className="h-2.5 bg-gray-200 rounded w-3/4 mb-1" />
                <div className="h-2.5 bg-gray-300 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </PhonePreview>
  );
}
